import { useState, useEffect } from 'react';
import { generateVideoThumbnail, isFileTypeVideo } from 'core/util';

// Simple in-memory cache for video thumbnails
const thumbnailCache = new Map<string, string>();

/**
 * Custom hook to generate and cache video thumbnails
 * @param videoUrl - URL of the video file
 * @param timeInSeconds - Time in seconds to capture the frame (default: 1)
 * @returns Object containing thumbnail URL, loading state, and error state
 */
export const useVideoThumbnail = (
  videoUrl: string,
  timeInSeconds: number = 1
) => {
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Only generate thumbnail for video files
    if (!videoUrl || !isFileTypeVideo(videoUrl)) {
      setThumbnail(null);
      setLoading(false);
      setError(null);
      return;
    }

    const cacheKey = `${videoUrl}_${timeInSeconds}`;

    // Check cache first
    if (thumbnailCache.has(cacheKey)) {
      setThumbnail(thumbnailCache.get(cacheKey)!);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    // Create video thumbnail if video thumbnail is not already generated
    generateVideoThumbnail(videoUrl, timeInSeconds)
      .then((thumbnailDataUrl) => {
        // Cache the result
        thumbnailCache.set(cacheKey, thumbnailDataUrl);
        setThumbnail(thumbnailDataUrl);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to generate video thumbnail:', err);
        setError(err.message || 'Failed to generate thumbnail');
        setLoading(false);
      });
  }, [videoUrl, timeInSeconds]);

  return { thumbnail, loading, error };
};
